// @ts-nocheck
/**
 * TeamLeader Workspace — Group Details Page
 * Scoped workspace for a single group: a persistent stat strip over three tabs —
 * Students (the roster, and where the page opens), Training and Messages.
 *
 * The Overview and Analytics tabs this page used to carry are gone: Overview was three
 * one-line cards and Analytics was two numbers, one of which repeated Overview's roster
 * count. Those figures now live in the stat strip, visible from every tab.
 */
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchMyGroupDetails,
  updateMyGroupInfo,
  addStudentToGroup,
  removeStudentFromGroup,
  assignTrackToGroup,
  removeTrackFromGroup,
  assignCourseToGroup,
  removeCourseFromGroup,
  messageGroup,
  lookupStudentByEmail,
  importStudentsCsvToGroup,
  fetchGroupProgressExport,
} from "@/lib/api/teamleader";
import { downloadStudentsCsvTemplate, fetchGroupStudents } from "@/lib/api/students";
import { buildGroupProgressCsv, progressCsvFileName } from "@/lib/group-progress-csv";
import { downloadCsv } from "@/lib/import-error-csv";
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import { PaginationControls } from "@/components/PaginationControls";
import { AssignableContentPicker } from "@/components/team/assignable-content-picker";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  Users,
  ArrowLeft,
  BookOpen,
  GraduationCap,
  MessageSquare,
  UserPlus,
  X,
  Trash2,
  Plus,
  Send,
  AlertTriangle,
  Search,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  Pencil,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/groups_/$groupId")({
  component: GroupDetailPage,
});

// Removed local fetchGroupStudentsList helper as we now use fetchGroupStudents from lib/api/students

// ── Roster helpers ───────────────────────────────────────────────────────────
// The status vocabulary here is the admin roster's (routes/_authenticated/admin/students.tsx):
// backend TrainingStatus enum names on the wire, human labels on screen, and the blacklist /
// not-responding flags taking precedence over the lifecycle value.
const BACKEND_TO_UI_STATUS: Record<string, string> = {
  InTraining: "In Training",
  Passed: "Passed Training",
  Failed: "Failed check",
  WithdrawnDuringTraining: "Withdraw",
  WithdrawnAfterTraining: "Withdraw after training",
  NotRecommended: "Not recommended",
};

const STATUS_BADGE: Record<string, string> = {
  "In Training": "bg-primary/10 text-primary border-primary/20",
  "Passed Training": "bg-success/15 text-success border-success/30",
  "Failed check": "bg-destructive-muted text-destructive border-destructive/25",
  Withdraw: "bg-warning-muted text-warning border-warning/30",
  "Withdraw after training": "bg-warning/15 text-warning border-warning/25",
  "Not recommended": "bg-destructive/10 text-destructive border-destructive/20",
  "Not responding": "bg-muted text-muted-foreground border-border",
  "Black list": "bg-destructive/20 text-destructive border-destructive/40",
};

// Filter values are the enum names the endpoint matches on, NOT the labels above — passing a
// label (or anything else the enum doesn't contain) silently matches no rows.
const STUDENT_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "InTraining", label: "In Training" },
  { value: "Passed", label: "Passed Training" },
  { value: "Failed", label: "Failed check" },
  { value: "WithdrawnDuringTraining", label: "Withdraw" },
  { value: "WithdrawnAfterTraining", label: "Withdraw after training" },
  { value: "NotRecommended", label: "Not recommended" },
];

// ActivityState (LMSAPI.DAL.Enums.ActivityState) is derived from LastSeenAt at query time.
const ACTIVITY_DOT: Record<string, string> = {
  Online: "bg-success",
  Offline: "bg-muted-foreground/40",
  NeverActive: "bg-warning",
};

/**
 * The subset of StudentDto (LMSAPI.BLL/Dtos/Student/StudentDto.cs) this roster reads, in the
 * camelCase the endpoint actually serialises. Named explicitly because reading these fields in
 * snake_case is exactly the bug this page had.
 */
type RosterStudent = {
  id: string;
  email?: string | null;
  fullName?: string | null;
  avatarUrl?: string | null;
  studentCode?: string | null;
  completedModules?: number | null;
  totalModules?: number | null;
  progressPercent?: number | null;
  trainingStatus?: string | null;
  isBlacklisted?: boolean;
  isNotResponding?: boolean;
  activity?: string | null;
  lastSeenAt?: string | null;
};

function rosterStatusLabel(student: RosterStudent): string {
  if (student.isBlacklisted) return "Black list";
  if (student.isNotResponding) return "Not responding";
  const raw = student.trainingStatus;
  if (!raw) return "";
  return BACKEND_TO_UI_STATUS[raw] ?? raw;
}

function rosterInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function lastActiveLabel(student: RosterStudent): string {
  if (student.activity === "Online") return "Online now";
  if (!student.lastSeenAt) return "Never opened";
  return formatDistanceToNow(new Date(student.lastSeenAt), { addSuffix: true });
}

function GroupDetailPage() {
  const { groupId } = useParams({ from: "/_authenticated/team-leader/groups_/$groupId" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The roster is what this page is for, and the Overview tab it used to open on held three
  // one-line cards whose numbers now sit in the stat strip above the tabs.
  const [activeTab, setActiveTab] = useState("students");

  // Warnings returned by an assign-track call (a track whose courses are already attached to the
  // group directly). They used to surface only as a toast that vanished before the leader could
  // act on the duplicate it was reporting.
  const [trackWarnings, setTrackWarnings] = useState<string[]>([]);

  // State for Edit Group modal
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  // State for Add Student modal
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [studentEmail, setStudentEmail] = useState("");
  const [foundStudent, setFoundStudent] = useState<any>(null);
  const [searchingStudent, setSearchingStudent] = useState(false);

  // State for Import CSV modal
  const [importOpen, setImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);

  // State for Assign Track modal
  const [assignTrackOpen, setAssignTrackOpen] = useState(false);

  // State for Assign Course modal
  const [assignCourseOpen, setAssignCourseOpen] = useState(false);

  // State for Message Group modal
  const [messageText, setMessageText] = useState("");

  // Pagination & Search state for Students Tab
  const [studentPage, setStudentPage] = useState(1);
  const [studentPageSize, setStudentPageSize] = useState(25);
  const [studentSearch, setStudentSearch] = useState("");
  // Server-side facet. Values are TrainingStatus enum names, which is what the endpoint matches on.
  const [studentStatus, setStudentStatus] = useState("all");

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ["tl", "group", groupId],
    queryFn: () => fetchMyGroupDetails(groupId),
    enabled: !!groupId,
  });

  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: [
      "tl",
      "group-students",
      groupId,
      studentPage,
      studentPageSize,
      studentSearch,
      studentStatus,
    ],
    queryFn: () =>
      fetchGroupStudents(
        groupId,
        studentPage,
        studentPageSize,
        studentSearch,
        studentStatus === "all" ? "" : studentStatus,
      ),
    enabled: !!groupId,
  });

  // Counts for the stat strip. pageSize=1 makes each of these a totalCount probe rather than a
  // roster fetch — the same trick fetchTLGroupsWithStudents uses for the group cards, and the
  // status values now match it too. The query this replaces asked for status "Active", which is
  // not a TrainingStatus the backend recognises, so "Active Learners" was always 0.
  //
  // Deliberately independent of the roster's search and status filter: the strip describes the
  // group, so it must not move when the leader filters the list below it.
  const { data: groupCounts } = useQuery({
    queryKey: ["tl", "group-counts", groupId],
    queryFn: async () => {
      const [total, inTraining, passed, atRisk] = await Promise.all([
        fetchGroupStudents(groupId, 1, 1),
        fetchGroupStudents(groupId, 1, 1, "", "InTraining"),
        fetchGroupStudents(groupId, 1, 1, "", "Passed"),
        fetch(`${API_BASE_URL}/Students/grid?groupId=${groupId}&isBlacklisted=true&pageSize=1`, {
          headers: getAuthHeaders(),
        }).then((res) => (res.ok ? res.json() : { totalCount: 0 })),
      ]);
      return {
        total: total?.totalCount ?? 0,
        inTraining: inTraining?.totalCount ?? 0,
        passed: passed?.totalCount ?? 0,
        atRisk: atRisk?.totalCount ?? 0,
      };
    },
    enabled: !!groupId,
    staleTime: 30_000,
  });

  const rosterTotal = groupCounts?.total ?? 0;
  const passedPercent =
    rosterTotal > 0 ? Math.round(((groupCounts?.passed ?? 0) / rosterTotal) * 100) : 0;

  const students = studentsData?.items || [];

  // ── Mutations ───────────────────────────────────────────────────────────

  const handleLookup = async () => {
    if (!studentEmail.trim()) return;
    setSearchingStudent(true);
    try {
      const result = await lookupStudentByEmail(studentEmail.trim());
      setFoundStudent(result);
      if (!result) toast.error("Student not found with that email");
    } catch (err: any) {
      toast.error(err.message || "Lookup failed");
    } finally {
      setSearchingStudent(false);
    }
  };

  const updateGroupMutation = useMutation({
    mutationFn: (body: { name: string; description?: string | null }) =>
      updateMyGroupInfo(groupId, body),
    onSuccess: () => {
      toast.success("Group details updated");
      qc.invalidateQueries({ queryKey: ["tl", "group", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "groups-with-students"] });
      setEditOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const addStudentMutation = useMutation({
    mutationFn: (studentId: string) => addStudentToGroup(groupId, studentId),
    onSuccess: () => {
      toast.success("Student added to group");
      qc.invalidateQueries({ queryKey: ["tl", "group-students", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "group-counts", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "groups-with-students"] });
      setAddStudentOpen(false);
      setStudentEmail("");
      setFoundStudent(null);
      setStudentPage(1); // Reset page to 1 on member add
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /**
   * Downloads the group's progress sheet: one row per member, one column per assigned track and
   * per course. Fetched on click rather than with the page — it is a whole-roster report (no
   * paging, and it crosses every student with every course), so nobody who never presses the
   * button should pay for it.
   */
  const exportProgress = useMutation({
    mutationFn: () => fetchGroupProgressExport(groupId),
    onSuccess: (report) => {
      if (report.students.length === 0) {
        toast.info("No students in this group yet — nothing to export.");
        return;
      }
      downloadCsv(
        buildGroupProgressCsv(report),
        progressCsvFileName(report.groupName, report.generatedAt),
      );
      toast.success(`Exported progress for ${report.students.length} student(s).`);
    },
    onError: (err: Error) => toast.error(err.message || "Could not build the progress export"),
  });

  const importCsvMutation = useMutation({
    mutationFn: (file: File) => importStudentsCsvToGroup(groupId, file),
    onSuccess: (result: any) => {
      setImportResult(result);
      qc.invalidateQueries({ queryKey: ["tl", "group-students", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "group-counts", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "groups-with-students"] });
      setStudentPage(1); // Reset page to 1 on import
      const ok = result?.successCount ?? 0;
      const failed = result?.failureCount ?? 0;
      if (failed > 0) toast.warning(`Imported ${ok} student(s), ${failed} row(s) failed.`);
      else toast.success(`Imported ${ok} student(s).`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeStudentMutation = useMutation({
    mutationFn: (studentId: string) => removeStudentFromGroup(groupId, studentId),
    onSuccess: () => {
      toast.success("Student removed from group");
      qc.invalidateQueries({ queryKey: ["tl", "group-students", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "group-counts", groupId] });
      qc.invalidateQueries({ queryKey: ["tl", "groups-with-students"] });
      setStudentPage(1); // Reset page to 1 on remove
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const assignTrackMutation = useMutation({
    mutationFn: (trackId: string) => assignTrackToGroup(groupId, trackId),
    onSuccess: (result: any) => {
      // A track whose courses overlap the group's direct links succeeds WITH warnings (204 becomes
      // 200 + { warnings }). Surfacing them is the point: the overlap is a real duplicate the leader
      // should clean up, and swallowing it would leave two provenance rows competing over which
      // certificate the student earns.
      // A toast is the wrong home for these: the leader is being told to go and detach a
      // duplicate, which they cannot do in the seconds before it disappears. Kept on the
      // Training tab until dismissed, and still toasted so the assign is acknowledged.
      const warnings: string[] = result?.warnings ?? [];
      setTrackWarnings(warnings);
      if (warnings.length > 0) toast.warning(`Track assigned with ${warnings.length} warning(s)`);
      else toast.success("Track assigned to group");
      qc.invalidateQueries({ queryKey: ["tl", "group", groupId] });
      qc.invalidateQueries({ queryKey: ["assignable"] });
      setAssignTrackOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeTrackMutation = useMutation({
    mutationFn: (trackId: string) => removeTrackFromGroup(groupId, trackId),
    onSuccess: () => {
      toast.success("Track removed from group");
      qc.invalidateQueries({ queryKey: ["tl", "group", groupId] });
      qc.invalidateQueries({ queryKey: ["assignable"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const assignCourseMutation = useMutation({
    mutationFn: (courseId: string) => assignCourseToGroup(groupId, courseId),
    onSuccess: () => {
      toast.success("Course assigned to group");
      qc.invalidateQueries({ queryKey: ["tl", "group", groupId] });
      qc.invalidateQueries({ queryKey: ["assignable"] });
      setAssignCourseOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeCourseMutation = useMutation({
    mutationFn: (courseId: string) => removeCourseFromGroup(groupId, courseId),
    onSuccess: () => {
      toast.success("Course removed from group");
      qc.invalidateQueries({ queryKey: ["tl", "group", groupId] });
      qc.invalidateQueries({ queryKey: ["assignable"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const messageGroupMutation = useMutation({
    mutationFn: (content: string) => messageGroup(groupId, content),
    onSuccess: () => {
      toast.success("Message sent to group!");
      setMessageText("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (groupLoading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
      {/* Back link — the unlabelled arrow button gave no clue where it went. */}
      <button
        type="button"
        onClick={() => navigate({ to: "/team-leader/groups" })}
        className="flex cursor-pointer items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-[15px] w-[15px]" />
        My Groups
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-3xl font-bold tracking-tight">
              {group?.name || "Group Workspace"}
            </h1>
            {group?.batchName && (
              <Badge
                variant="outline"
                className="shrink-0 text-[11px] font-semibold text-muted-foreground"
              >
                Batch {group.batchName}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            {group?.description || "Scoped management workspace"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Edit Group Modal */}
          <Dialog
            open={editOpen}
            onOpenChange={(o) => {
              setEditOpen(o);
              if (o) {
                setEditName(group?.name ?? "");
                setEditDesc(group?.description ?? "");
              }
            }}
          >
            <DialogTrigger asChild>
              {/* Ghost, not outline: three same-weight buttons gave the destructive-adjacent
                  Import and the everyday Add Student equal billing. */}
              <Button size="sm" variant="ghost" className="gap-2 text-muted-foreground">
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Group Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Group name</label>
                  <Input
                    placeholder="Group name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <textarea
                    className="w-full min-h-[100px] p-3 border rounded-lg bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Optional description"
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEditOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!editName.trim() || updateGroupMutation.isPending}
                    onClick={() =>
                      updateGroupMutation.mutate({
                        name: editName.trim(),
                        description: editDesc.trim() || null,
                      })
                    }
                  >
                    {updateGroupMutation.isPending ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Student Modal */}
          <Dialog open={addStudentOpen} onOpenChange={setAddStudentOpen}>
            <DialogTrigger asChild>
              {/* order-last keeps the one primary action at the end of the row without moving
                  these sibling Dialog blocks around. */}
              <Button size="sm" className="gap-2 sm:order-last">
                <UserPlus className="h-4 w-4" />
                Add Student
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Student to {group?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter student email..."
                    value={studentEmail}
                    onChange={(e) => setStudentEmail(e.target.value)}
                  />
                  <Button variant="outline" onClick={handleLookup} disabled={searchingStudent}>
                    Search
                  </Button>
                </div>
                {foundStudent && (
                  <div className="p-3 border rounded-lg flex items-center justify-between bg-accent/40">
                    <div>
                      <p className="font-semibold text-sm">
                        {foundStudent.full_name || foundStudent.email}
                      </p>
                      <p className="text-xs text-muted-foreground">{foundStudent.email}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addStudentMutation.mutate(foundStudent.id)}
                      disabled={addStudentMutation.isPending}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => exportProgress.mutate()}
            disabled={exportProgress.isPending}
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exportProgress.isPending ? "Preparing…" : "Export progress"}
          </Button>

          {/* Import CSV Modal */}
          <Dialog
            open={importOpen}
            onOpenChange={(o) => {
              setImportOpen(o);
              if (!o) {
                setCsvFile(null);
                setImportResult(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-2">
                <Upload className="h-4 w-4" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Import students into {group?.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  Upload a CSV to bulk-add students to this group. New accounts are created and
                  linked automatically; existing students are just added to the group.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => downloadStudentsCsvTemplate()}
                >
                  <Download className="h-4 w-4" /> Download template
                </Button>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-4 hover:bg-accent/40">
                  <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm">{csvFile ? csvFile.name : "Choose a .csv file…"}</span>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                      setImportResult(null);
                      setCsvFile(e.target.files?.[0] ?? null);
                    }}
                  />
                </label>

                {importResult && (
                  <div className="space-y-2 rounded-lg border p-3 text-sm">
                    <div className="flex items-center gap-2 font-medium flex-wrap">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                      {/* An existing student added to this group is reported separately from a
                          newly-created account — it used to be counted as a failure. */}
                      {importResult.successCount} created
                      {(importResult.linkedExistingCount ?? 0) > 0 &&
                        `, ${importResult.linkedExistingCount} existing student(s) added`}
                      {(importResult.alreadyInGroupCount ?? 0) > 0 &&
                        `, ${importResult.alreadyInGroupCount} already in this group`}
                      {importResult.failureCount > 0 && `, ${importResult.failureCount} failed`} of{" "}
                      {importResult.totalRows} row(s)
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="max-h-40 space-y-1 overflow-y-auto">
                        {importResult.errors.map((err: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-destructive">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                              Row {err.row}
                              {err.email ? ` (${err.email})` : ""}: {err.errorMessage}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setImportOpen(false)}>
                    Close
                  </Button>
                  <Button
                    className="gap-2"
                    disabled={!csvFile || importCsvMutation.isPending}
                    onClick={() => csvFile && importCsvMutation.mutate(csvFile)}
                  >
                    <Upload className="h-4 w-4" />
                    {importCsvMutation.isPending ? "Importing…" : "Import"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stat strip — shows on every tab, and replaces both the Overview tab (three one-line
          cards) and the Analytics tab (two numbers, one of which repeated Overview's roster
          count). */}
      <Card className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4">
          <div className="min-w-0">
            <p className="text-2xl font-bold leading-7 text-foreground">{rosterTotal}</p>
            <p className="truncate text-[11px] font-medium leading-[14px] text-muted-foreground">
              Students on the roster
            </p>
          </div>
          <div className="min-w-0 border-l pl-5">
            <p className="text-2xl font-bold leading-7 text-foreground">
              {groupCounts?.inTraining ?? 0}
            </p>
            <p className="truncate text-[11px] font-medium leading-[14px] text-muted-foreground">
              In training
            </p>
          </div>
          <div className="min-w-0 pt-4 sm:border-l sm:pl-5 sm:pt-0">
            <p className="text-2xl font-bold leading-7 text-success">{groupCounts?.passed ?? 0}</p>
            <p className="truncate text-[11px] font-medium leading-[14px] text-muted-foreground">
              Passed training
            </p>
          </div>
          <div className="min-w-0 border-l pl-5 pt-4 sm:pt-0">
            <p className="text-2xl font-bold leading-7 text-destructive">
              {groupCounts?.atRisk ?? 0}
            </p>
            <p className="truncate text-[11px] font-medium leading-[14px] text-muted-foreground">
              At risk
            </p>
          </div>
        </div>
        <div className="mt-3.5 border-t pt-3.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Passed training
            </span>
            <span className="text-[13px] font-semibold text-foreground">
              {passedPercent}%
              <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                ({groupCounts?.passed ?? 0} of {rosterTotal})
              </span>
            </span>
          </div>
          <Progress value={passedPercent} className="mt-1.5 h-2" />
        </div>
      </Card>

      {/* Workspace Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted p-1 rounded-xl">
          <TabsTrigger value="students" className="gap-2">
            <GraduationCap className="h-4 w-4" /> Students ({rosterTotal})
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-2">
            <BookOpen className="h-4 w-4" /> Training
          </TabsTrigger>
          <TabsTrigger value="messages" className="gap-2">
            <MessageSquare className="h-4 w-4" /> Messages
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="space-y-4">
          {/* Roster toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students in this group..."
                value={studentSearch}
                onChange={(e) => {
                  setStudentSearch(e.target.value);
                  setStudentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={studentStatus}
              onValueChange={(v) => {
                setStudentStatus(v);
                setStudentPage(1);
              }}
            >
              <SelectTrigger className="w-[190px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STUDENT_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-[13px]">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {studentsData && studentsData.totalCount > 0 && (
              <span className="sm:ml-auto text-[13px] text-muted-foreground">
                Showing {(studentPage - 1) * studentPageSize + 1}–
                {(studentPage - 1) * studentPageSize + students.length} of {studentsData.totalCount}
              </span>
            )}
          </div>

          {studentsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : students.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="font-semibold">
                  {studentSearch || studentStatus !== "all"
                    ? "No students match these filters"
                    : "No students in this group yet"}
                </p>
                <p className="text-xs mt-1">
                  {studentSearch || studentStatus !== "all"
                    ? "Try a different search term or status."
                    : 'Use the "Add Student" button above to enroll members.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              {/* One column header. Hidden below xl, where the row wraps and no single header
                  line can stay aligned with the cells. */}
              <div className="hidden items-center gap-4 bg-muted/35 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground xl:flex">
                <span className="min-w-0 flex-1">Student</span>
                <span className="w-[120px] shrink-0">Code</span>
                <span className="w-[180px] shrink-0">Module progress</span>
                <span className="w-[140px] shrink-0">Training status</span>
                <span className="w-[120px] shrink-0">Last active</span>
                <span className="w-[100px] shrink-0" />
              </div>

              <div className="divide-y divide-muted/50">
                {students.map((student: RosterStudent) => {
                  // These reads were snake_case (full_name, status). The endpoint serialises a
                  // camelCase StudentDto, so `full_name` was always undefined — every row fell
                  // back to the email — and `status` was too, so every badge read "active".
                  // Same field-name bug the group cards had, and fixing it is what makes the
                  // rest of this row possible: progress, code and activity were already on the
                  // wire and simply thrown away.
                  const name = student.fullName || student.email || "Unnamed student";
                  const total = student.totalModules ?? 0;
                  const done = student.completedModules ?? 0;
                  const pct = Math.round(student.progressPercent ?? 0);
                  const status = rosterStatusLabel(student);

                  return (
                    <div
                      key={student.id}
                      className="flex flex-wrap items-center gap-x-4 gap-y-3 bg-card px-4 py-3 transition-colors hover:bg-accent/30"
                    >
                      {/* Identity */}
                      <div className="flex min-w-[220px] flex-1 items-center gap-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-[13px] font-semibold text-primary">
                          {student.avatarUrl ? (
                            <img
                              src={student.avatarUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            rosterInitials(name)
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-foreground">
                              {name}
                            </span>
                            <span
                              className={`h-2 w-2 shrink-0 rounded-full ${
                                ACTIVITY_DOT[student.activity] ?? "bg-muted-foreground/40"
                              }`}
                              title={lastActiveLabel(student)}
                            />
                          </div>
                          <p className="truncate text-xs text-muted-foreground">{student.email}</p>
                        </div>
                      </div>

                      {/* Student code */}
                      <div className="hidden w-[120px] shrink-0 md:block">
                        <p className="truncate text-[12.5px] font-medium text-foreground">
                          {student.studentCode || "—"}
                        </p>
                      </div>

                      {/* Module progress */}
                      <div className="w-[180px] shrink-0">
                        {total > 0 ? (
                          <>
                            <p className="text-[11px] font-medium leading-[14px] text-foreground">
                              {done}/{total} ({pct}%)
                            </p>
                            <Progress value={pct} className="mt-1.5 h-1.5 bg-muted" />
                          </>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">No modules assigned</p>
                        )}
                      </div>

                      {/* Training status */}
                      <div className="w-[140px] shrink-0">
                        {status ? (
                          <Badge
                            variant="outline"
                            className={`max-w-full truncate text-[11px] font-medium ${
                              STATUS_BADGE[status] ?? "bg-muted text-muted-foreground border-border"
                            }`}
                          >
                            {status}
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </div>

                      {/* Last active */}
                      <div className="hidden w-[120px] shrink-0 xl:block">
                        <p className="truncate text-xs text-muted-foreground">
                          {lastActiveLabel(student)}
                        </p>
                      </div>

                      {/* Row actions */}
                      <div className="flex w-[100px] shrink-0 items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2.5 text-xs text-primary hover:bg-primary/10 hover:text-primary"
                          onClick={() =>
                            navigate({
                              to: "/team-leader/students/$studentId",
                              params: { studentId: student.id },
                            })
                          }
                        >
                          Profile
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          title={`Remove ${name} from this group`}
                          onClick={() => removeStudentMutation.mutate(student.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {studentsData && (
                <div className="border-t px-2">
                  <PaginationControls
                    pageNumber={studentPage}
                    pageSize={studentPageSize}
                    totalPages={studentsData.totalPages}
                    totalCount={studentsData.totalCount}
                    hasNextPage={studentsData.hasNextPage}
                    hasPreviousPage={studentsData.hasPreviousPage}
                    onPageChange={setStudentPage}
                    onPageSizeChange={(size) => {
                      setStudentPageSize(size);
                      setStudentPage(1);
                    }}
                    isLoading={studentsLoading}
                  />
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        {/* ── TRAINING TAB ──────────────────────────────────────────────── */}
        <TabsContent value="training" className="space-y-4">
          {/* Overlap warnings from the last assign-track call. These ask the leader to go and
              detach a duplicate, which is not something they can do in the few seconds a toast
              stays on screen — so they persist here until dismissed. */}
          {trackWarnings.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning-muted px-3.5 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-[12.5px] font-semibold leading-[17px] text-foreground">
                  {trackWarnings.length === 1
                    ? "This track overlaps a course already attached directly"
                    : `This track overlaps ${trackWarnings.length} courses already attached directly`}
                </p>
                {trackWarnings.map((w, i) => (
                  <p key={i} className="text-[12.5px] leading-[17px] text-foreground">
                    {w}
                  </p>
                ))}
                <p className="text-[12.5px] leading-[17px] text-muted-foreground">
                  Students still see each course once, but two links now claim it. Detaching the
                  direct one below leaves the track as the single source.
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                title="Dismiss"
                onClick={() => setTrackWarnings([])}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tracks Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Assigned Tracks</CardTitle>
                  <CardDescription>Learning tracks assigned to this group</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() => setAssignTrackOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" /> Assign Track
                </Button>
                <AssignableContentPicker
                  kind="track"
                  groupId={groupId}
                  groupName={group?.name}
                  open={assignTrackOpen}
                  onOpenChange={setAssignTrackOpen}
                  onSelect={(id) => assignTrackMutation.mutate(id)}
                  isPending={assignTrackMutation.isPending}
                  pendingId={assignTrackMutation.variables ?? null}
                />
              </CardHeader>
              <CardContent>
                {group?.tracks && group.tracks.length > 0 ? (
                  <div className="space-y-2">
                    {group.tracks.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <span className="font-medium text-sm">{t.name}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeTrackMutation.mutate(t.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">
                    No tracks assigned to this group.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Courses Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Assigned Courses</CardTitle>
                  <CardDescription>Courses explicitly assigned to this group</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() => setAssignCourseOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" /> Assign Course
                </Button>
                <AssignableContentPicker
                  kind="course"
                  groupId={groupId}
                  groupName={group?.name}
                  open={assignCourseOpen}
                  onOpenChange={setAssignCourseOpen}
                  onSelect={(id) => assignCourseMutation.mutate(id)}
                  isPending={assignCourseMutation.isPending}
                  pendingId={assignCourseMutation.variables ?? null}
                />
              </CardHeader>
              <CardContent>
                {group?.courses && group.courses.length > 0 ? (
                  <div className="space-y-2">
                    {group.courses.map((c: any) => (
                      <div
                        key={c.id}
                        // amber-500 was the only colour on this page reaching outside the brand
                        // palette; --warning is the token for exactly this state.
                        className={`flex items-center justify-between p-3 border rounded-lg ${
                          c.isPublished === false ? "border-warning/40 bg-warning/[0.06]" : ""
                        }`}
                      >
                        <span className="font-medium text-sm flex items-center gap-2 min-w-0">
                          <span className="truncate">{c.name}</span>
                          {/* A draft course stays listed so it can be detached, but it must be
                              obvious that students are not receiving it. */}
                          {c.isPublished === false && (
                            <Badge
                              variant="outline"
                              className="text-[10px] shrink-0 border-warning/40 text-warning"
                              title="Unpublished — hidden from students until it is published"
                            >
                              Draft · not visible to students
                            </Badge>
                          )}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeCourseMutation.mutate(c.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">
                    No individual courses assigned.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── MESSAGES TAB ──────────────────────────────────────────────── */}
        <TabsContent value="messages">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Send className="h-4 w-4 text-primary" /> Send Group Message
              </CardTitle>
              <CardDescription>
                Broadcast an announcement to all students in {group?.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                className="w-full min-h-[140px] p-4 border rounded-xl bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder={`Type your message to ${group?.name}...`}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <div className="flex justify-end">
                <Button
                  className="gap-2"
                  disabled={!messageText.trim() || messageGroupMutation.isPending}
                  onClick={() => messageGroupMutation.mutate(messageText)}
                >
                  <Send className="h-4 w-4" /> Send Announcement
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
